import { Button, PageHeader, useToast } from '@fruuit/ui'
import { Star } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { FILTER_KEYS } from '../../features/coverage/api'
import { CoverageRegister } from '../../features/coverage/CoverageRegister'
import { usePreferences, useSavePreferences } from '../../features/preferences/api'
import { useTitle } from '../../lib/useTitle'

/** S2 Portfolio coverage (investor): every holding in one connected view (I-09, P-COV-001). */
export function PortfolioPage() {
  useTitle('Portfolio')
  const [params, setParams] = useSearchParams()
  const preferences = usePreferences()
  const { save, saving } = useSavePreferences()
  const { toast } = useToast()
  const defaultPortfolio = preferences.data?.preferences.default_portfolio_id ?? null
  const selected = params.get('portfolio')

  // Open on the default portfolio (I-06) once, and only when the URL carries no filters,
  // so shared links and dashboard deep links always win.
  const applied = useRef(false)
  useEffect(() => {
    if (applied.current || !preferences.data) return
    applied.current = true
    if (defaultPortfolio && !FILTER_KEYS.some((key) => params.has(key))) {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          next.set('portfolio', defaultPortfolio)
          return next
        },
        { replace: true },
      )
    }
  }, [preferences.data, defaultPortfolio, params, setParams])

  async function setDefault(value: string | null) {
    const saved = await save({ default_portfolio_id: value })
    if (!saved) toast({ tone: 'error', message: 'The default portfolio could not be saved.' })
    else
      toast({ tone: 'success', message: value ? 'Default portfolio saved.' : 'Default cleared.' })
  }

  const action =
    preferences.data && selected ? (
      selected === defaultPortfolio ? (
        <Button variant="ghost" size="sm" loading={saving} onClick={() => setDefault(null)}>
          Clear default portfolio
        </Button>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          icon={<Star />}
          loading={saving}
          onClick={() => setDefault(selected)}
        >
          Make this my default portfolio
        </Button>
      )
    ) : undefined

  return (
    <>
      <PageHeader
        eyebrow="Portfolio coverage"
        title="Every holding. One connected view."
        description="Filter the portfolio, review the finding and open the complete company record."
        actions={action}
      />
      <CoverageRegister org={null} base="/app" />
    </>
  )
}
